import { useMutation, useQueryClient } from '@tanstack/react-query'
import { FileText, Image as ImageIcon, Loader2, Paperclip, Send, X } from 'lucide-react'
import { useRef, useState, type ChangeEvent, type FormEvent } from 'react'

import {
  addQuoteComment,
  apiUrl,
  QuoteLinkError,
  quoteLinkQueryOptions,
  type SharedComment,
  type SharedCommentFile,
  type SharedQuoteLine,
  type SharedQuoteView,
} from '@/api/quote'
import { LineImage } from '@/components/quote/quote-document'
import { Button } from '@/components/ui/button'
import { InlineEditField } from '@/components/ui/inline-edit'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { ctaButton } from '@/lib/brand'
import { commentTag, FILE_ACCEPT, fileProblem, fileSize, lineTitle, MAX_FILES, rememberName } from '@/lib/quote'
import { cn } from '@/lib/utils'

const commentTime = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
})
const sendErrors: Record<string, string> = {
  stale_publication: 'A newer version of this quote is available. Refresh the page, then send your comment.',
  line_not_published: 'That item is no longer on this quote. Refresh the page.',
  file_too_large: 'Files can be up to 10 MB.',
  unsupported_file_type: 'Send photos (JPG, PNG, GIF, WebP), PDFs, Excel, CSV, Word or text files.',
  too_many_files: `Send up to ${MAX_FILES} files at a time.`,
  empty_file: 'That file is empty.',
}

/**
 * The quote's comments beside the page (USA-1345): one item's comments when opened from its comment
 * icon, every comment from the header. The customer's comments sit on the right, Sales's answers on
 * the left. Comments sent here are tagged to the item, with any photos and files.
 */
export function CommentsPanel({
  token,
  data,
  open,
  line,
  name,
  onNameChange,
  onOpenChange,
}: {
  token: string
  data: SharedQuoteView
  open: boolean
  /** The item whose comments are shown; null for all of them. */
  line: SharedQuoteLine | null
  name: string
  onNameChange: (name: string) => void
  onOpenChange: (open: boolean) => void
}) {
  const queryClient = useQueryClient()
  const key = line?.row_ref ?? ''
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [attached, setAttached] = useState<Record<string, File[]>>({})
  const [fileError, setFileError] = useState<string | null>(null)
  const draft = drafts[key] ?? ''
  const files = attached[key] ?? []
  const panel = useRef<HTMLDivElement>(null)
  const commentInput = useRef<HTMLTextAreaElement>(null)
  const comments = line
    ? data.comments.filter((item) => item.row_ref === line.row_ref)
    : data.comments

  const addComment = useMutation({
    mutationFn: ({ rowRef, body, files }: { rowRef: string | null; body: string; files: File[] }) =>
      addQuoteComment(token, data.publication.publication_number, name.trim(), body, rowRef, files),
    onSuccess: (_comment, { rowRef }) => {
      rememberName(name.trim())
      setDrafts((current) => ({ ...current, [rowRef ?? '']: '' }))
      setAttached((current) => ({ ...current, [rowRef ?? '']: [] }))
      void queryClient.invalidateQueries({ queryKey: quoteLinkQueryOptions(token).queryKey })
    },
  })
  const error = addComment.error && (addComment.variables?.rowRef ?? '') === key ? addComment.error : null

  const attach = (event: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(event.target.files ?? [])
    event.target.value = ''
    const next = [...files, ...picked]
    const problem = fileProblem(next)
    setFileError(problem)
    if (!problem) setAttached((current) => ({ ...current, [key]: next }))
  }

  return (
    <Sheet onOpenChange={onOpenChange} open={open}>
      <SheetContent
        aria-describedby={undefined}
        onEscapeKeyDown={(event) => {
          // Escape in the name field puts the name back instead of closing the panel.
          if (event.target instanceof HTMLElement && 'inlineEdit' in event.target.dataset) event.preventDefault()
        }}
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          // Phones keep the keyboard down until the customer taps the box.
          const fine = window.matchMedia('(pointer: fine)').matches
          ;(fine && name.trim() ? commentInput : panel).current?.focus()
        }}
        ref={panel}
      >
        <header className="flex min-w-0 items-center gap-3 border-b py-4 pr-14 pl-5">
          {line?.primary_asset?.url ? (
            <span className="w-11 shrink-0">
              <LineImage icon={ImageIcon} line={line} />
            </span>
          ) : null}
          <SheetTitle className="min-w-0 break-words">
            {line ? lineTitle(line) : 'Questions & comments'}
          </SheetTitle>
        </header>
        {/* Reversed scroller around one block: it opens on the newest comment, even as photos load. */}
        <div className="flex min-h-24 flex-1 flex-col-reverse overflow-y-auto overscroll-contain">
          <div className="grid gap-4 px-5 py-5">
            {comments.length ? (
              comments.map((item) => (
                <Comment item={item} key={item.id} tag={line ? null : commentTag(item, data.quote.lines)} />
              ))
            ) : (
              <p className="text-muted-foreground py-6 text-center text-sm">No comments yet.</p>
            )}
          </div>
        </div>
        <form
          className="grid gap-2.5 border-t px-5 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]"
          onSubmit={(event: FormEvent) => {
            event.preventDefault()
            if (!addComment.isPending)
              addComment.mutate({ rowRef: line?.row_ref ?? null, body: draft.trim(), files })
          }}
        >
          <fieldset className="grid min-w-0 gap-2.5" disabled={addComment.isPending}>
            <div className="flex min-h-9 min-w-0 items-center gap-2 text-sm">
              <span className="text-muted-foreground shrink-0">Your name</span>
              <span className="min-w-0 flex-1">
                <InlineEditField
                  autoComplete="name"
                  label="Your name"
                  maxLength={200}
                  onChange={onNameChange}
                  value={name}
                />
              </span>
            </div>
            <textarea
              aria-label="Comment"
              className="focus:border-usa-blue focus:ring-usa-blue/15 bg-background min-h-20 w-full resize-y rounded-md border px-3 py-2.5 text-sm outline-none focus:ring-2"
              maxLength={5000}
              onChange={(event) => setDrafts((current) => ({ ...current, [key]: event.target.value }))}
              placeholder="Ask a question or leave a note for our team"
              ref={commentInput}
              value={draft}
            />
            {files.length ? (
              <ul aria-label="Attached files" className="flex flex-wrap gap-1.5">
                {files.map((file, index) => (
                  <li
                    className="bg-muted flex max-w-full min-w-0 items-center gap-1 rounded-md py-0.5 pr-0.5 pl-2 text-xs"
                    key={`${file.name}-${index}`}
                  >
                    <span className="min-w-0 truncate">{file.name}</span>
                    <span className="text-muted-foreground shrink-0">{fileSize(file.size)}</span>
                    <button
                      aria-label={`Remove ${file.name}`}
                      className="hover:bg-background grid size-7 shrink-0 place-items-center rounded"
                      onClick={() => {
                        setFileError(null)
                        setAttached((current) => ({ ...current, [key]: files.filter((_file, at) => at !== index) }))
                      }}
                      type="button"
                    >
                      <X aria-hidden="true" className="size-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            <div className="flex items-center justify-between gap-3">
              <label
                className={cn(
                  'text-usa-navy hover:bg-muted focus-within:outline-usa-blue -ml-2 inline-flex h-10 cursor-pointer items-center gap-1.5 rounded-md px-2 text-sm font-medium focus-within:outline-2',
                  files.length >= MAX_FILES && 'pointer-events-none opacity-50',
                )}
              >
                <Paperclip aria-hidden="true" className="size-4" />
                Attach
                <input
                  accept={FILE_ACCEPT}
                  className="sr-only"
                  disabled={files.length >= MAX_FILES}
                  multiple
                  onChange={attach}
                  type="file"
                />
              </label>
              <Button
                className={ctaButton}
                disabled={(!draft.trim() && !files.length) || !name.trim()}
                type="submit"
              >
                {addComment.isPending ? (
                  <Loader2 aria-hidden="true" className="animate-spin motion-reduce:animate-none" />
                ) : (
                  <Send aria-hidden="true" />
                )}
                Send comment
              </Button>
            </div>
          </fieldset>
          {fileError || error ? (
            <p className="text-destructive text-sm" role="alert">
              {fileError ??
                (error instanceof QuoteLinkError && sendErrors[error.code]) ??
                'Your comment could not be sent. Try again.'}
            </p>
          ) : null}
        </form>
      </SheetContent>
    </Sheet>
  )
}

/** One comment: the customer's on the right in our blue, Sales's answers on the left. */
function Comment({ item, tag }: { item: SharedComment; tag: string | null }) {
  const mine = !item.from_staff
  return (
    <article className={cn('flex min-w-0 flex-col gap-1', mine ? 'items-end' : 'items-start')}>
      <div
        className={cn(
          'grid max-w-[88%] min-w-0 gap-1 rounded-2xl px-3.5 py-2.5 text-sm',
          mine ? 'bg-usa-blue rounded-br-md text-white' : 'bg-muted rounded-bl-md',
        )}
      >
        {tag ? (
          <p className={cn('text-xs font-semibold break-words', mine ? 'text-white/85' : 'text-usa-blue')}>
            @{tag}
          </p>
        ) : null}
        {item.body ? <p className="break-words whitespace-pre-wrap">{item.body}</p> : null}
        {item.files.length ? <CommentFiles files={item.files} mine={mine} /> : null}
      </div>
      {/* Who answered for us; the customer's own comments need only the time. */}
      <p className="text-muted-foreground px-1 text-xs">
        {mine ? null : `${item.author_name} · `}
        <time dateTime={item.created_at}>{commentTime.format(new Date(item.created_at))}</time>
      </p>
    </article>
  )
}

function CommentFiles({ files, mine }: { files: SharedCommentFile[]; mine: boolean }) {
  const photos = files.filter((file) => file.kind === 'image')
  const documents = files.filter((file) => file.kind !== 'image')
  return (
    <div className="mt-1 grid gap-1.5">
      {photos.length ? (
        <div className="flex flex-wrap gap-1.5">
          {photos.map((file) => (
            // eslint-disable-next-line no-restricted-syntax -- a file on our API, opened in a new tab
            <a
              className="focus-visible:outline-usa-blue block overflow-hidden rounded-lg focus-visible:outline-2"
              href={apiUrl(file.url)}
              key={file.id}
              rel="noreferrer"
              target="_blank"
            >
              <img
                alt={file.filename}
                className="bg-background size-28 object-cover"
                loading="lazy"
                src={apiUrl(file.url)}
              />
            </a>
          ))}
        </div>
      ) : null}
      {documents.map((file) => (
        // eslint-disable-next-line no-restricted-syntax -- a file on our API, opened in a new tab
        <a
          className={cn(
            'flex min-w-0 items-center gap-2 rounded-lg px-2.5 py-2 text-xs font-medium hover:underline',
            mine ? 'bg-white/15' : 'bg-background',
          )}
          href={apiUrl(file.url)}
          key={file.id}
          rel="noreferrer"
          target="_blank"
        >
          <FileText aria-hidden="true" className="size-4 shrink-0" />
          <span className="min-w-0 truncate">{file.filename}</span>
          <span className={cn('shrink-0', mine ? 'text-white/75' : 'text-muted-foreground')}>
            {fileSize(file.size_bytes)}
          </span>
        </a>
      ))}
    </div>
  )
}
